/**
 * Estados comunes para requests de datos de una feature.
 * `data === null` representa que todavía no hay datos para mostrar;
 * una respuesta vacía válida (por ejemplo, `[]`) debe conservarse como dato.
 */
export type AsyncStatus = 'idle' | 'loading' | 'refreshing' | 'success' | 'error';

export interface AsyncState<T> {
  readonly status: AsyncStatus;
  readonly data: T | null;
  readonly error: unknown | null;
  readonly lastUpdated: number | null;
}

export function createAsyncState<T>(data: T | null = null): AsyncState<T> {
  return {
    status: data === null ? 'idle' : 'success',
    data,
    error: null,
    lastUpdated: data === null ? null : Date.now(),
  };
}

/** Starts the first request or a non-blocking refresh when data exists. */
export function beginAsyncRequest<T>(state: AsyncState<T>): AsyncState<T> {
  return {
    ...state,
    status: state.data === null ? 'loading' : 'refreshing',
    error: null,
  };
}

export function completeAsyncRequest<T>(
  state: AsyncState<T>,
  data: T,
  lastUpdated = Date.now(),
): AsyncState<T> {
  return {
    status: 'success',
    data,
    error: null,
    lastUpdated,
  };
}

/**
 * Preserves usable data on refresh errors so the feature can show a local,
 * non-blocking warning. With no data, the same error remains retryable.
 */
export function failAsyncRequest<T>(
  state: AsyncState<T>,
  error: unknown,
): AsyncState<T> {
  return {
    ...state,
    status: 'error',
    error,
  };
}

export function resetAsyncState<T>(): AsyncState<T> {
  return createAsyncState<T>();
}
